import React, { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { CheckoutData } from '../../pages/checkout/HostingCheckout';
import { HostingPackage } from '../../services/hostingService';
import { stripePromise } from '../../config/stripe';
import { getApiUrl } from '../../config/environment';
import StripePaymentForm from './StripePaymentForm';

/** Safe numeric coercion — prices may be undefined/strings mid-flow. */
const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : undefined;
};
const money = (v: unknown) => (num(v) ?? 0).toFixed(2);
const round2 = (n: number) => Math.round(n * 100) / 100;

interface ServerPricing {
  servicePrice?: number;
  emailPrice?: number;
  hostingPrice?: number;
  vpsPrice?: number;
  domainPrice?: number;
  discount?: number;
  total?: number;
}

interface PaymentStepProps {
  data: CheckoutData;
  package: HostingPackage;
  onNext: (data: Partial<CheckoutData>) => void;
  onBack: () => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({ data, package: pkg, onNext, onBack }) => {
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [orderCreated, setOrderCreated] = useState<any>(null);
  // When the server rejects our totals it returns its own figures; we show
  // those and let the customer review before retrying.
  const [serverPricing, setServerPricing] = useState<ServerPricing | null>(null);
  const [pricingRefreshed, setPricingRefreshed] = useState(false);

  // The HostingPackage row carries the truth — `pkg.type` is
  // 'hosting' | 'shared' | 'vps' | 'email' …; anything that isn't email/vps
  // is a hosting order for the backend gate.
  const rawType = String((pkg as any)?.type || 'hosting');
  const pkgType: 'hosting' | 'vps' | 'email' =
    rawType === 'email' ? 'email' : rawType === 'vps' ? 'vps' : 'hosting';

  // Display + item price. Prefer the explicit service figure PlanStep set,
  // then the per-product compatibility fields. Every value is guarded so a
  // missing field renders as $0.00 instead of crashing on `.toFixed`.
  const basePricing: any = data.pricing || {};
  const effective = {
    servicePrice: num(serverPricing?.servicePrice)
      ?? num(basePricing.emailPrice) ?? num(basePricing.servicePrice) ?? num(basePricing.hostingPrice) ?? 0,
    domainPrice: num(serverPricing?.domainPrice) ?? num(basePricing.domainPrice) ?? 0,
    discount: num(serverPricing?.discount) ?? num(basePricing.discount) ?? 0,
    total: 0,
    yearlySavings: num(basePricing.yearlySavings) ?? 0,
  };
  // For email, emailPrice is the authoritative figure; for VPS/hosting the
  // product-specific field wins when present.
  if (!serverPricing) {
    if (pkgType === 'email') effective.servicePrice = num(basePricing.emailPrice) ?? num(basePricing.servicePrice) ?? 0;
    else if (pkgType === 'vps') effective.servicePrice = num(basePricing.vpsPrice) ?? num(basePricing.servicePrice) ?? num(basePricing.hostingPrice) ?? 0;
    else effective.servicePrice = num(basePricing.hostingPrice) ?? num(basePricing.servicePrice) ?? 0;
  }
  effective.total = num(serverPricing?.total)
    ?? num(basePricing.total)
    ?? round2(effective.servicePrice + effective.domainPrice);

  const createOrder = async () => {
    if (orderCreated) return orderCreated; // Return existing order if already created

    // F4: We require a real JWT before reaching this step. Pages enforce
    // login upstream; this is a defensive double-check. If a user somehow
    // reaches PaymentStep without a token, abort with a clear message
    // instead of silently creating an account with a random password.
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be signed in to complete checkout. Please log in and try again.');
    }

    const serviceType = pkgType;
    const servicePrice = effective.servicePrice;
    const domainPrice = effective.domainPrice;
    const total = effective.total;

    const itemsData: any = {
      [pkgType]: {
        package_id: data.packageId,
        package_name: data.packageName,
        billing_cycle: data.billingCycle,
        price: servicePrice
      }
    };

    // Add domain if being registered
    if (data.domainAction === 'register' && domainPrice > 0) {
      itemsData.domain = {
        domain_name: data.domainName,
        action: data.domainAction,
        price: domainPrice
      };
    }

    // Pricing contract shared with the server:
    //   total = servicePrice + domainPrice; emailPrice/hostingPrice/vpsPrice
    //   mirror servicePrice for the matching product and are 0 otherwise.
    const pricingDetails = {
      servicePrice,
      emailPrice:   pkgType === 'email'   ? servicePrice : 0,
      hostingPrice: pkgType === 'hosting' ? servicePrice : 0,
      vpsPrice:     pkgType === 'vps'     ? servicePrice : 0,
      domainPrice,
      discount: effective.discount,
      total,
    };

    const orderData = {
      // Required fields that backend expects (camelCase)
      packageId: data.packageId,
      domainName: data.domainName,
      domainAction: data.domainAction,
      billingCycle: data.billingCycle,
      contactInfo: data.contactInfo,
      pricingDetails,
      paymentMethod: (paymentMethod === 'card' ? 'stripe' : 'paypal') as 'stripe' | 'paypal',
      serviceType, // Add service type for backend processing

      // Additional fields for order record
      customer_id: null, // Will be set by backend based on token
      total_amount: total,
      currency: 'USD',
      status: 'pending',
      payment_status: 'pending',
      billing_address: data.contactInfo.address,
      items: itemsData
    };

    // Called directly (not via orderService) so we can read the 400 body:
    // the server includes `serverPricing` when our totals don't match.
    let resp: Response;
    try {
      resp = await fetch(getApiUrl('/orders'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(orderData),
      });
    } catch (e: any) {
      throw new Error(e?.message || 'Network error while creating your order');
    }
    const body: any = await resp.json().catch(() => ({}));

    if (resp.status === 400 && body && body.serverPricing) {
      setServerPricing(body.serverPricing as ServerPricing);
      setPricingRefreshed(true);
      throw new Error('Prices were refreshed — please review and continue.');
    }
    if (!resp.ok || !body?.success) {
      throw new Error(body?.error || body?.message || `Failed to create order (HTTP ${resp.status})`);
    }

    setOrderCreated(body.data);
    return body.data;
  };

  const handlePaymentSuccess = (paymentIntentId: string) => {
    console.log('✅ Payment completed successfully:', paymentIntentId);
    
    onNext({
      orderId: orderCreated.id,
      orderNumber: orderCreated.order_number,
      paymentIntentId
    });
  };

  const handlePaymentError = (error: string) => {
    console.error('❌ Payment failed:', error);
    setErrors({ payment: error });
  };

  const handleCreateOrder = async () => {
    if (paymentMethod === 'paypal') {
      setErrors({ payment: 'PayPal integration not yet implemented. Please use card payment.' });
      return;
    }

    setErrors({});
    setPricingRefreshed(false);

    try {
      await createOrder();
    } catch (error: any) {
      setErrors({ payment: error.message || 'Failed to create order' });
    }
  };

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
          <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
          <span className="text-sm font-semibold text-gray-700">Step 3: Payment</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
          Secure <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Payment</span>
        </h2>
        
        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          
          <div className="space-y-3">
            {pricingRefreshed && (
              <div role="status" aria-live="polite" className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-900">
                Prices were refreshed — please review and continue.
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{pkg.name} - {data.billingCycle === 'yearly' ? 'Annual' : 'Monthly'}</span>
              <span className="font-medium">${money(effective.servicePrice)}</span>
            </div>
            
            {(data.domainAction === 'register' || data.domainAction === 'transfer') && effective.domainPrice > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">
                  {data.domainAction === 'register' 
                    ? `Domain Registration (${data.domainName})` 
                    : `Domain Transfer (${data.domainName}) + 1 Year Extension`
                  }
                </span>
                <span className="font-medium">${money(effective.domainPrice)}</span>
              </div>
            )}
            
            {effective.discount > 0 && (
              <div className="flex justify-between items-center text-green-700">
                <span>Discount</span>
                <span>-${money(effective.discount)}</span>
              </div>
            )}

            {data.billingCycle === 'yearly' && effective.yearlySavings > 0 && !serverPricing && (
              <div className="flex justify-between items-center text-green-700 text-sm">
                <span>Annual savings vs monthly (already included)</span>
                <span>${money(effective.yearlySavings)}</span>
              </div>
            )}
            
            <div className="border-t pt-3">
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total</span>
                <span className="text-brand-orange">${money(effective.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Method Selection */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
          
          <div className="space-y-3">
            <label className={`flex items-center p-4 border rounded-lg cursor-pointer ${
              paymentMethod === 'card' ? 'border-brand-orange bg-orange-50' : 'border-gray-200'
            }`}>
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                checked={paymentMethod === 'card'}
                onChange={(e) => setPaymentMethod(e.target.value as 'card')}
                className="h-4 w-4 text-brand-orange focus:ring-brand-orange border-gray-300"
              />
              <div className="ml-3 flex items-center">
                <span className="font-medium">Credit/Debit Card</span>
                <div className="ml-4 flex space-x-2">
                  <img src="/images/visa.png" alt="Visa" className="h-6" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  <img src="/images/mastercard.png" alt="Mastercard" className="h-6" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  <img src="/images/amex.png" alt="American Express" className="h-6" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              </div>
            </label>
            
            <label className={`flex items-center p-4 border rounded-lg cursor-pointer ${
              paymentMethod === 'paypal' ? 'border-brand-orange bg-orange-50' : 'border-gray-200'
            }`}>
              <input
                type="radio"
                name="paymentMethod"
                value="paypal"
                checked={paymentMethod === 'paypal'}
                onChange={(e) => setPaymentMethod(e.target.value as 'paypal')}
                className="h-4 w-4 text-brand-orange focus:ring-brand-orange border-gray-300"
              />
              <div className="ml-3 flex items-center">
                <span className="font-medium">PayPal</span>
                <img src="/images/paypal.png" alt="PayPal" className="ml-4 h-6" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
            </label>
          </div>
        </div>

        {/* Stripe Payment Form - Order creation handled by bottom navigation */}

        {paymentMethod === 'card' && orderCreated && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
            <Elements stripe={stripePromise}>
              <StripePaymentForm
                amount={effective.total}
                orderId={orderCreated.id}
                customerEmail={data.contactInfo.email}
                customerName={`${data.contactInfo.firstName} ${data.contactInfo.lastName}`}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                isProcessing={processing}
                setIsProcessing={setProcessing}
              />
            </Elements>
          </div>
        )}

        {/* PayPal Information */}
        {paymentMethod === 'paypal' && (
          <div className="mb-8 p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-800">
              You will be redirected to PayPal to complete your payment securely.
            </p>
          </div>
        )}

        {/* Error Display */}
        {errors.payment && (
          <div role="alert" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{errors.payment}</p>
          </div>
        )}

        {/* Security Notice */}
        <div className="mb-8 p-4 bg-green-50 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-sm text-green-800">
              Your payment is secured with 256-bit SSL encryption
            </span>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={onBack}
            disabled={processing}
            className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-md disabled:opacity-50"
          >
            Back to Plan
          </button>
          <button
            onClick={handleCreateOrder}
            disabled={processing || orderCreated}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group disabled:opacity-50"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">
              {processing ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                orderCreated ? 'Order Created - Complete Payment Above' : `Continue to Payment - $${money(effective.total)}`
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;